import { getIdToken, User } from "firebase/auth";
import { app, auth, appCheck } from "./firebaseConfig"; // Import 'app', 'auth', and 'appCheck' from your firebaseConfig file
import axios from "axios";
// --- CORRECTED APP CHECK IMPORTS ---
import { getToken } from "firebase/app-check";

const API_BASE_URL = import.meta.env.VITE_APP_SERVER;


// --- Create a reusable Axios instance ---
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-store",
        "Pragma": "no-cache",
        "Expires": "0"
    },
});

// --- Use an interceptor to automatically add tokens to every request ---
apiClient.interceptors.request.use(
    async (config) => {
        // 1. Add Firebase Auth token if a user is logged in
        const user: User | null = auth.currentUser;
        if (user) {
            try {
                const token = await getIdToken(user);
                config.headers.Authorization = `Bearer ${token}`;
            } catch (error) {
                console.error("Could not get user auth token.", error);
            }
        }

        // 2. Add Firebase App Check token
        if (appCheck) {
            try {
                const appCheckTokenResponse = await getToken(appCheck);
                config.headers["X-Firebase-AppCheck"] = appCheckTokenResponse.token;
                if (import.meta.env.DEV) {
                    console.log("✅ App Check token added to request");
                }
            } catch (firstError) {
                console.warn("App Check token fetch failed, retrying with forced refresh...", firstError);
                try {
                    const retryResponse = await getToken(appCheck, true);
                    config.headers["X-Firebase-AppCheck"] = retryResponse.token;
                } catch (retryError) {
                    console.error("❌ App Check token fetch failed after retry:", retryError);
                    if (import.meta.env.DEV) {
                        console.warn("⚠️  Development mode: Continuing without App Check token");
                    } else {
                        throw retryError;
                    }
                }
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

const toApiError = (error: any) => {
    const response = error?.response;
    if (!response) return error;

    const rawHeaders = response.headers || {};
    const headers = typeof rawHeaders?.toJSON === 'function' ? rawHeaders.toJSON() : rawHeaders;
    const creditStatus =
        rawHeaders?.['x-sarvalok-credit-status'] ||
        rawHeaders?.['X-Sarvalok-Credit-Status'] ||
        rawHeaders?.get?.('x-sarvalok-credit-status') ||
        rawHeaders?.get?.('X-Sarvalok-Credit-Status');

    const data = response.data;
    const payload =
        data && typeof data === 'object' && !Array.isArray(data)
            ? { ...data }
            : { detail: data ?? error?.message };

    return {
        ...payload,
        status: response.status,
        headers,
        creditStatus,
    };
};

// --- Helper for fetch requests (like chatfn) that can't use the interceptor ---
export const getSecureHeaders = async () => {
    const headers: HeadersInit = {
        "Content-Type": "application/json",
        "Cache-Control": "no-store",
        "Pragma": "no-cache",
        "Expires": "0"
    };

    const user: User | null = auth.currentUser;
    if (user) {
        try {
            const token = await getIdToken(user);
            headers['Authorization'] = `Bearer ${token}`;
        } catch (error) {
            console.error("Could not get user auth token for fetch.", error);
        }
    }

    if (appCheck) {
        try {
            const appCheckTokenResponse = await getToken(appCheck);
            headers['X-Firebase-AppCheck'] = appCheckTokenResponse.token;
            if (import.meta.env.DEV) {
                console.log("✅ App Check token added to fetch headers");
            }
        } catch (error) {
        console.error("❌ Failed to get App Check token for fetch:", error);
        if (import.meta.env.DEV) {
            console.warn("⚠️  Development mode: Continuing without App Check token");
            console.log("🔧 This may cause Firebase operations to fail if App Check is enforced");
        } else {
            throw error; // In production, we want to fail if App Check token is not available
        }
        }
    }

    return headers;
};




// --- Refactored API Functions ---

// Chat streaming API (uses fetch, so we use the helper)
export const chatfn = async (
    payload: any,
    useDirect: boolean = false,
    opts?: { signal?: AbortSignal }
) => {
    try {
        const headers = await getSecureHeaders();
        const endpoint = useDirect ? '/direct_stream' : '/stream';
        const res = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: "POST",
            headers,
            cache: "no-store",
            body: JSON.stringify(payload),
            signal: opts?.signal,
        });

        if (!res.ok) {
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                const errorData = await res.json();
                throw new Error(`${errorData.detail}`);
            } else {
                throw new Error(`HTTP error! status: ${res.status}`);
            }
        }
        return res;
    } catch (error: any) {
        console.error("Error in chatfn:", error.message || error);
        throw error;
    }
};

// --- Progressive Context Expansion ---
export const expandAnswer = async (
    payload: { expansion_token: string; previous_answer: string },
    opts?: { signal?: AbortSignal }
) => {
    try {
        const headers = await getSecureHeaders();
        const res = await fetch(`${API_BASE_URL}/expand`, {
            method: "POST",
            headers,
            cache: "no-store",
            body: JSON.stringify(payload),
            signal: opts?.signal,
        });

        if (!res.ok) {
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                const errorData = await res.json();
                throw new Error(`${errorData.detail}`);
            } else {
                throw new Error(`HTTP error! status: ${res.status}`);
            }
        }
        return res;
    } catch (error: any) {
        console.error("Error in expandAnswer:", error.message || error);
        throw error;
    }
};

// --- Reading Mode ---
export const readSource = async (
    payload: { reading_token: string; direction: string; locale?: string; narrate?: boolean; step?: number },
    opts?: { signal?: AbortSignal }
) => {
    try {
        const headers = await getSecureHeaders();
        const res = await fetch(`${API_BASE_URL}/read`, {
            method: "POST",
            headers,
            cache: "no-store",
            body: JSON.stringify(payload),
            signal: opts?.signal,
        });

        if (!res.ok) {
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                const errorData = await res.json();
                throw new Error(`${res.status}: ${errorData.detail}`);
            } else {
                throw new Error(`HTTP error! status: ${res.status}`);
            }
        }
        return res;
    } catch (error: any) {
        console.error("Error in readSource:", error.message || error);
        throw error;
    }
};

// --- New function to fetch filters from the backend ---
export const getFilters = async () => {
    try {
        // This endpoint is public on the backend and does not require any auth/appcheck headers.
        const res = await apiClient.get("/api/filters");
        return res.data;
    } catch (error: any) {
        console.error("Error in getFilters:", error);
        throw error?.response?.data || error;
    }
};

// All other APIs now use the clean apiClient instance
export const createUser = async (payload: { user_id: string; username: string }) => {
    try {
        const res = await apiClient.post("/users/", payload);
        return res.data;
    } catch (error: any) {
        console.error("Error in createUser:", error);
        throw error?.response?.data || error;
    }
};

export type EnsureUserDocPayload = {
    firstName?: string;
    lastName?: string;
    displayName?: string;
    email?: string;
    provider?: string;
    source?: string;
    signup_device?: string;
    signup_browser?: string;
    signup_os?: string;
    signup_screen_width?: number;
};

export const ensureUserDocument = async (payload: EnsureUserDocPayload = {}) => {
    try {
        const res = await apiClient.post("/users/ensure", payload);
        return res.data as { status: number; user: Record<string, any> };
    } catch (error: any) {
        console.error("Error in ensureUserDocument:", error);
        const message = error?.response?.data?.detail || error?.message || 'Unable to verify this account.';
        const ensureError = new Error(message) as Error & { status?: number; data?: unknown };
        ensureError.status = error?.response?.status;
        ensureError.data = error?.response?.data;
        throw ensureError;
    }
};

export const createChatThread = async (payload: {
    user_id: string;
    user_name: string;
    personal_mode?: boolean;
    active_subject_id?: string;
    active_subject_mode?: 'single' | 'compat_pair';
    addressed_subject_id?: string | null;
    compat_subject_ids?: string[];
}) => {
    try {
        const res = await apiClient.post("/threads/", payload);
        return res.data;
    } catch (error: any) {
        console.error("Error in createChatThread:", error);
        throw toApiError(error);
    }
};

export const getUserThreads = async (userId: string) => {
    try {
        // This was a POST in the original file, so keeping it as POST
        const res = await apiClient.post(`/users/${userId}/threads/`);
        return res.data;
    } catch (error: any) {
        console.error("Error in getUserThreads:", error);
        throw toApiError(error);
    }
};

export const getUserThreadsMetadata = async (userId: string) => {
    try {
        const res = await apiClient.post(`/users/${userId}/threads/metadata/`);
        return res.data;
    } catch (error: any) {
        console.error("Error in getUserThreadsMetadata:", error);
        throw toApiError(error);
    }
};

// ==================================================================================
// === THIS IS THE CORRECTED FUNCTION WITH THE 'footnotes' FIELD ADDED ===
// ==================================================================================
export const addMessageToThread = async (
    threadId: string, 
    payload: { 
        question: string; 
        text: string; 
        user_id: string; 
        title: string; 
        footnotes?: any; // ✅ FIX: Added the optional footnotes field
        personal_mode?: boolean;
        is_dinacharya?: boolean;
        dinacharya_generated_for_date?: string | null;
        static_dinacharya_library_version?: string | null;
        dinacharya_subject_display_name?: string | null;
        dinacharya_birth_details?: {
            name?: string;
            birthDate?: string;
            birthTime?: string;
            location?: string;
        } | null;
        dinacharya_timeline_payload?: any | null;
        dinacharya_timeline_mode?: 'basic' | 'advanced' | null;
        dinacharya_timeline_date?: string | null;
    }
) => {
    try {
        const res = await apiClient.post(`/threads/${threadId}/messages/`, payload);
        return res.data;
    } catch (error: any) {
        console.error("Error in addMessageToThread:", error);
        throw toApiError(error);
    }
};

export const getThreadMessages = async (
    threadId: string,
    userId: string,
    opts?: { limit?: number; before_turn_index?: number; around_turn_index?: number },
) => {
    try {
        // storage v2: paginated. No cursor → latest `limit` turns; `before_turn_index`
        // → an older page; `around_turn_index` → a window centred on a turn (bookmark jump).
        // Response may carry `old_history_unavailable:true` for pre-v2 threads.
        const params: Record<string, any> = { user_id: userId };
        if (opts?.limit != null) params.limit = opts.limit;
        if (opts?.before_turn_index != null) params.before_turn_index = opts.before_turn_index;
        if (opts?.around_turn_index != null) params.around_turn_index = opts.around_turn_index;
        const res = await apiClient.post(`/threads/${threadId}/messages/get`, null, { params });
        return res.data;
    } catch (error: any) {
        console.error("Error in getThreadMessages:", error);
        throw toApiError(error);
    }
};

export const deleteThread = async (threadId: string, userId: string) => {
    try {
        const res = await apiClient.delete(`/threads/${threadId}/`, { params: { user_id: userId } });
        return res.data;
    } catch (error: any) {
        console.error("Error in deleteThread:", error);
        throw error?.response?.data || error;
    }
};

export const renameThread = async (threadId: string, userId: string, newTitle: string) => {
    try {
        const res = await apiClient.put(`/threads/${threadId}/rename/`, null, { params: { user_id: userId, new_title: newTitle } });
        return res.data;
    } catch (error: any) {
        console.error("Error in renameThread:", error);
        throw error?.response?.data || error;
    }
};

export const updateThreadSubject = async (
    threadId: string,
    payload: {
        active_subject_id: string;
        active_subject_mode?: 'single' | 'compat_pair';
        addressed_subject_id?: string | null;
        compat_subject_ids?: string[];
    }
) => {
    try {
        const res = await apiClient.put(`/threads/${threadId}/subject/`, payload);
        return res.data;
    } catch (error: any) {
        console.error("Error in updateThreadSubject:", error);
        throw error?.response?.data || error;
    }
};

export const getMemory = async () => {
    try {
        const res = await apiClient.get('/memory');
        return res.data;
    } catch (error: any) {
        console.error("Error in getMemory:", error);
        throw error?.response?.data || error;
    }
};

export const exportMemory = async () => {
    try {
        const res = await apiClient.get('/memory/export');
        return res.data;
    } catch (error: any) {
        console.error("Error in exportMemory:", error);
        throw error?.response?.data || error;
    }
};

export const createMemory = async (payload: {
    text: string;
    item_type?: string;
    category?: string;
    subject_id?: string;
    status?: string | null;
    sensitivity?: string | null;
    thread_id?: string | null;
    metadata?: Record<string, any>;
}) => {
    try {
        const res = await apiClient.post('/memory', payload);
        return res.data;
    } catch (error: any) {
        console.error("Error in createMemory:", error);
        throw error?.response?.data || error;
    }
};

export const updateMemory = async (itemId: string, payload: {
    text?: string;
    item_type?: string;
    category?: string;
    subject_id?: string;
    status?: string | null;
    sensitivity?: string | null;
    metadata?: Record<string, any> | null;
}) => {
    try {
        const res = await apiClient.patch(`/memory/${encodeURIComponent(itemId)}`, payload);
        return res.data;
    } catch (error: any) {
        console.error("Error in updateMemory:", error);
        throw error?.response?.data || error;
    }
};

export const deleteMemory = async (itemId: string) => {
    try {
        const res = await apiClient.delete(`/memory/${encodeURIComponent(itemId)}`);
        return res.data;
    } catch (error: any) {
        console.error("Error in deleteMemory:", error);
        throw error?.response?.data || error;
    }
};

export const clearMemory = async (payload: { subject_id?: string | null }) => {
    try {
        const res = await apiClient.delete('/memory/all', { data: payload });
        return res.data;
    } catch (error: any) {
        console.error("Error in clearMemory:", error);
        throw error?.response?.data || error;
    }
};

export const createBookmark = async (payload: { user_id: string; thread_id: string; turn_index?: number; message_id?: string; message_index?: number; question?: string; answer_text?: string; answer_snippet?: string }) => {
    try {
        const res = await apiClient.post("/bookmarks/", payload);
        return res.data;
    } catch (error: any) {
        console.error("Error in createBookmark:", error);
        throw error?.response?.data || error;
    }
};

export const getUserBookmarks = async (userId: string, limit: number = 200) => {
    try {
        const res = await apiClient.post(`/users/${userId}/bookmarks/`, null, { params: { limit } });
        return res.data;
    } catch (error: any) {
        console.error("Error in getUserBookmarks:", error);
        throw error?.response?.data || error;
    }
};

export const deleteBookmark = async (bookmarkId: string, userId: string) => {
    try {
        const res = await apiClient.delete(`/bookmarks/${encodeURIComponent(bookmarkId)}/`, { params: { user_id: userId } });
        return res.data;
    } catch (error: any) {
        console.error("Error in deleteBookmark:", error);
        throw error?.response?.data || error;
    }
};

// Payment APIs
export const createSubscription = async (payload: { plan_id: string; customer_name: string; customer_email: string; customer_contact: string; uid: string; is_annual: boolean; }) => {
    try {
        const res = await apiClient.post("/create-subscription", payload);
        return res.data;
    } catch (error: any) {
        console.error("Error in createSubscription:", error);
        throw error?.response?.data || error;
    }
};

export const getSubscriptionStatus = async (subscriptionId: string) => {
    try {
        const res = await apiClient.post(`/subscription-status/${subscriptionId}`);
        return res.data;
    } catch (error: any) {
        console.error("Error in getSubscriptionStatus:", error);
        throw error?.response?.data || error;
    }
};

export const checkSubscriptionRenewal = async (uid: string) => {
    try {
        const res = await apiClient.post(`/check-subscription-renewal/${uid}`);
        return res.data;
    } catch (error: any) {
        console.error("Error in checkSubscriptionRenewal:", error);
        throw error?.response?.data || error;
    }
};

export const createOrder = async (payload: { amount: number; currency: string; uid: string }) => {
    try {
        const res = await apiClient.post("/create-order", payload);
        return res.data;
    } catch (error: any) {
        console.error("Error in createOrder:", error);
        throw error?.response?.data || error;
    }
};

export const getBillingGateway = async () => {
    try {
        const res = await apiClient.get("/billing/gateway");
        return res.data as { gateway: "razorpay" | "stripe"; country_code?: string | null; country_source?: string | null };
    } catch (error: any) {
        console.error("Error in getBillingGateway:", error);
        throw error?.response?.data || error;
    }
};

export const stripeCreateSubscriptionIntent = async (payload: { uid: string; plan_name: string; is_annual: boolean }) => {
    try {
        const res = await apiClient.post("/stripe/create-subscription-intent", payload);
        return res.data as { subscription_id: string; client_secret: string };
    } catch (error: any) {
        console.error("Error in stripeCreateSubscriptionIntent:", error);
        throw error?.response?.data || error;
    }
};

export const stripeCreateTopUpIntent = async (payload: { uid: string }) => {
    try {
        const res = await apiClient.post("/stripe/create-topup-intent", payload);
        return res.data as { payment_intent_id: string; client_secret: string };
    } catch (error: any) {
        console.error("Error in stripeCreateTopUpIntent:", error);
        throw error?.response?.data || error;
    }
};

export const stripeChangePlan = async (payload: { uid: string; plan_name: string; is_annual: boolean }) => {
    try {
        const res = await apiClient.post("/stripe/change-plan", payload);
        return res.data as { subscription_id: string; client_secret?: string | null; payment_required: boolean; hits: number | string };
    } catch (error: any) {
        console.error("Error in stripeChangePlan:", error);
        throw error?.response?.data || error;
    }
};

export const cancelSubscription = async () => {
    try {
        const res = await apiClient.post("/cancel-subscription");
        return res.data as { status: "ok"; gateway: "razorpay" | "stripe"; cancel_at_period_end?: boolean | null };
    } catch (error: any) {
        console.error("Error in cancelSubscription:", error);
        throw error?.response?.data || error;
    }
};

export const resumeSubscription = async () => {
    try {
        const res = await apiClient.post("/resume-subscription");
        return res.data as { status: "ok"; gateway: "razorpay" | "stripe"; cancel_at_period_end?: boolean | null };
    } catch (error: any) {
        console.error("Error in resumeSubscription:", error);
        throw error?.response?.data || error;
    }
};

export const verifyOrder = async (payload: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string; uid: string; }) => {
    try {
        const res = await apiClient.post("/verify-order", payload);
        return res.data;
    } catch (error: any) {
        console.error("Error in verifyOrder:", error);
        throw error?.response?.data || error;
    }
};

// Public contact form (will still get App Check token but no auth token)
export const contactForm = async (data: { email: string; message: string; name: string; phone: string; }) => {
    const response = await apiClient.post("/send-email", data);
    return response.data;
};

export const submitFeedback = async (data: { type: 'bug' | 'idea' | 'help'; message: string; page_url: string; user_agent: string; thread_id?: string; }) => {
    const response = await apiClient.post("/api/feedback", data);
    return response.data;
};

export const changePlan = async (data: { subscription_id: string; period: number; new_plan_id: string; new_plan_name: string; uid: string; }): Promise<{
    status: 'success';
    subscription_id: string;
    new_plan: string;
    period: number;
    hits: number | string;
}> => {
    const response = await apiClient.post("/change-plan", data);
    return response.data;
};

export const verifyPayment = async (data: { razorpay_subscription_id: string; razorpay_payment_id: string; razorpay_signature: string; subscription_name: string; uid: string; }) => {
    const response = await apiClient.post("/verify-payment", data);
    return response.data;
};

export const syncSubscription = async (uid: string) => {
    try {
        const headers = await getSecureHeaders();
        const response = await fetch(`${API_BASE_URL}/sync-subscription/${uid}`, {
            method: 'POST', // Kept as POST to match your backend endpoint
            headers,
            cache: 'no-store'
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to sync subscription');
        }
        return response.json();
    } catch (error: any) {
        console.error("Error in syncSubscription:", error);
        throw error;
    }
};

// OCR image extraction API
export const ocrImage = async (file: File, locale?: string, prompt?: string) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        if (locale) formData.append('locale', locale);
        if (prompt) formData.append('prompt', prompt);

        const res = await apiClient.post('/api/ocr', formData);
        return res.data;
    } catch (error: any) {
        console.error('Error in ocrImage:', error);
        throw error?.response?.data || error;
    }
};

// ─────────────────────────────────────────────────────────────────────
// Birthplace autocomplete
// ─────────────────────────────────────────────────────────────────────

/** Row shape returned by the backend. Deliberately mirrors the legacy
 *  Nominatim response so `LocationInput` and every saved `birthChartData` /
 *  `boyFormData` / `girlFormData` record keeps working unchanged. */
export interface LocationSuggestion {
    place_id: string;
    display_name: string;
    /** Decimal STRINGS — consumers parse these; numbers would change form state. */
    lat: string;
    lon: string;
    type?: string;
    class?: string;
    address?: {
        city?: string;
        state?: string;
        county?: string;
        country?: string;
        country_code?: string;
    };
    /** Provider timezone block. `name` is the IANA zone and the only field the
     *  birth flow uses — the offsets here describe TODAY at that place, which is
     *  precisely NOT what a birth chart needs. The offset in force at the birth
     *  instant comes from `resolveBirthTimezone` below. */
    timezone?: {
        name: string;
        offset_std?: string;
        offset_dst?: string;
        offset_std_seconds?: number;
        offset_dst_seconds?: number;
    };
}

/** Outcome of resolving a birth instant against a place's timezone history. */
export interface ResolvedBirthTimezone {
    /** 'resolved' is the only status safe to compute a chart from. */
    status: 'resolved' | 'ambiguous' | 'nonexistent' | 'manual_required' | 'invalid';
    timezone_name?: string | null;
    offset_seconds?: number | null;
    offset_hours?: number | null;
    /** Pre-formatted for display, e.g. "UTC+05:30" or "UTC+05:21:10". */
    offset_label?: string | null;
    source?: string | null;
    confidence?: string | null;
    fold?: number | null;
    detail?: string | null;
    /** Present when status === 'ambiguous': the two candidate offsets. */
    choices?: Array<{
        fold: number;
        offset_seconds: number;
        offset_hours: number;
        label: string;
    }> | null;
    resolver_version?: number;
    tzdata_version?: string;
}

/**
 * Resolve the UTC offset that was in force at a birth instant.
 *
 * WHY THIS EXISTS: a birthplace's offset is not a property of the place alone —
 * it changes with DST and with historical zone changes. New York was UTC-5 in
 * December 1966 and is UTC-4 today. Asking a "what time is it there now" API,
 * which is what this replaces, answers the wrong question entirely.
 *
 * Advisory only. The backend re-resolves on every calculation route, so a stale
 * or tampered client cannot pin a wrong offset onto a chart; this call exists so
 * the form can show the user what will be used before they submit.
 */
export const resolveBirthTimezone = async (
    params: {
        localDate: string;
        localTime: string;
        timezoneName?: string | null;
        latitude?: number | null;
        longitude?: number | null;
        fold?: number | null;
    },
    opts: { signal?: AbortSignal } = {},
): Promise<ResolvedBirthTimezone> => {
    const res = await apiClient.post(
        '/api/location/resolve-timezone',
        {
            local_date: params.localDate,
            local_time: params.localTime,
            timezone_name: params.timezoneName ?? null,
            latitude: params.latitude ?? null,
            longitude: params.longitude ?? null,
            fold: params.fold ?? null,
        },
        { signal: opts.signal, timeout: 12000 },
    );
    return res.data as ResolvedBirthTimezone;
};

/** Minimum characters before a search is worth a request. Mirrors the backend's
 *  own `MIN_QUERY_LEN`; exported so `LocationInput` gates on the same number
 *  rather than a second literal that could drift out of step with the API. */
export const MIN_CITY_QUERY_LEN = 3;

export type LocationSearchErrorKind = 'unavailable' | 'rate_limited' | 'timeout' | 'unknown';

export class LocationSearchError extends Error {
    readonly kind: LocationSearchErrorKind;
    constructor(kind: LocationSearchErrorKind, message: string) {
        super(message);
        this.name = 'LocationSearchError';
        this.kind = kind;
    }
}

/**
 * Search settlements for a partial place name.
 *
 * Goes through `apiClient` so the Firebase ID token and App Check header are
 * attached automatically and the provider key never reaches the browser
 * bundle. Replaces BOTH previous direct Nominatim calls in `LocationInput`
 * (the debounced dropdown and the hidden `limit=1` resolver).
 *
 * Throws `LocationSearchError` with a kind so the UI can distinguish "no such
 * place" from "provider down" — collapsing those is what makes a search box
 * feel broken.
 */
export const searchLocations = async (
    query: string,
    opts: { language?: string; limit?: number; signal?: AbortSignal } = {},
): Promise<{ results: LocationSuggestion[]; attribution: string; provider: string }> => {
    const trimmed = String(query || '').trim();
    if (trimmed.length < MIN_CITY_QUERY_LEN) {
        return { results: [], attribution: 'geoapify_osm', provider: 'geoapify' };
    }
    try {
        const res = await apiClient.post(
            '/api/location/autocomplete',
            { query: trimmed, language: opts.language, limit: opts.limit ?? 8 },
            { signal: opts.signal, timeout: 12000 },
        );
        return {
            results: res.data?.results ?? [],
            attribution: res.data?.attribution ?? 'geoapify_osm',
            provider: res.data?.provider ?? 'geoapify',
        };
    } catch (error: any) {
        // An abort is the debouncer superseding this request, not a failure.
        if (
            error?.code === 'ERR_CANCELED' ||
            error?.name === 'CanceledError' ||
            error?.name === 'AbortError'
        ) {
            throw error;
        }
        const status = error?.response?.status;
        if (status === 429) throw new LocationSearchError('rate_limited', 'Too many searches.');
        if (status === 504 || error?.code === 'ECONNABORTED') {
            throw new LocationSearchError('timeout', 'Search timed out.');
        }
        if (status === 503 || status === 502) {
            throw new LocationSearchError('unavailable', 'Place search is unavailable.');
        }
        throw new LocationSearchError('unknown', 'Could not search places.');
    }
};
